import { isNil, reject as rReject, pipe, toPairs, map, join } from 'ramda';
import { createEvent } from 'effector';
import { HttpError, NetworkError } from './HttpError';
import type { AuthHeaderProvider, HttpQuery, HttpRequest } from './types';

const API_URL = (process.env.API_URL ?? '').replace(/\/+$/, '');

const isJsonResponse = (res: Response) =>
  (res.headers.get('content-type') ?? '').toLowerCase().includes('application/json');

const buildQueryString = (query?: HttpQuery): string => {
  if (!query) return '';
  const cleaned = rReject(isNil, query) as Record<string, string | number | boolean>;
  const parts = pipe(
    toPairs,
    map(
      ([k, v]: [string, string | number | boolean]) =>
        `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    ),
    join('&'),
  )(cleaned);
  return parts.length ? `?${parts}` : '';
};

const buildUrl = (path: string, query?: HttpQuery): string => {
  const isAbsolute = /^https?:\/\//i.test(path);
  const base = isAbsolute ? path : `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  return `${base}${buildQueryString(query)}`;
};

const parseBody = async <T>(res: Response): Promise<T | null> => {
  if (res.status === 204 || res.status === 205) return null;
  if (isJsonResponse(res)) return (await res.json()) as T;
  const text = await res.text();
  return (text || null) as T | null;
};

let authHeaderProvider: AuthHeaderProvider = () => ({});
export const setAuthHeaderProvider = (provider: AuthHeaderProvider) => {
  authHeaderProvider = provider;
};

export const httpUnauthorized = createEvent<HttpError>();
export const httpServerError = createEvent<HttpError>();
export const httpNetworkError = createEvent<NetworkError>();

export const request = async <T>(req: HttpRequest): Promise<T> => {
  const { method = 'GET', path, query, body, headers, signal, auth = true } = req;

  const url = buildUrl(path, query);
  const hasBody = !isNil(body) && method !== 'GET';
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...(auth ? authHeaderProvider() : {}),
    ...(headers ?? {}),
  };

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: finalHeaders,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal,
      credentials: 'same-origin',
    });
  } catch (cause) {
    if ((cause as DOMException)?.name === 'AbortError') throw cause;
    const err = new NetworkError('Network request failed', cause);
    httpNetworkError(err);
    throw err;
  }

  if (!response.ok) {
    const data = await parseBody<unknown>(response).catch(() => null);
    const err = new HttpError({
      status: response.status,
      statusText: response.statusText,
      url,
      data,
    });
    if (err.isUnauthorized) httpUnauthorized(err);
    else if (err.isServerError) httpServerError(err);
    throw err;
  }

  return (await parseBody<T>(response)) as T;
};

export const httpClient = {
  get: <T>(path: string, init?: Omit<HttpRequest, 'method' | 'path' | 'body'>) =>
    request<T>({ ...init, method: 'GET', path }),
  post: <T>(path: string, body?: unknown, init?: Omit<HttpRequest, 'method' | 'path' | 'body'>) =>
    request<T>({ ...init, method: 'POST', path, body }),
  put: <T>(path: string, body?: unknown, init?: Omit<HttpRequest, 'method' | 'path' | 'body'>) =>
    request<T>({ ...init, method: 'PUT', path, body }),
  patch: <T>(path: string, body?: unknown, init?: Omit<HttpRequest, 'method' | 'path' | 'body'>) =>
    request<T>({ ...init, method: 'PATCH', path, body }),
  delete: <T>(path: string, init?: Omit<HttpRequest, 'method' | 'path' | 'body'>) =>
    request<T>({ ...init, method: 'DELETE', path }),
};

export type HttpClient = typeof httpClient;
